import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  FlatList,
  TextInput,
  ActivityIndicator,
  Platform,
  ScrollView,
} from 'react-native';
import { InsetModal as Modal } from '../InsetModal';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { Ionicons } from '@expo/vector-icons';
import { apiService } from '../../services/api';
import { colors, spacing, typography } from '../../theme';
import { designTokens } from '../../theme/design-system';
import { withAlpha } from '../../theme';

interface UnitPickerProps {
  value?: string;           // currently selected unit name (displayed as label)
  unitId?: string;          // selected unit _id
  onSelect: (unit: { _id: string; name: string; abbreviation?: string } | null) => void;
  placeholder?: string;
  label?: string;
}

interface UnitOption {
  _id: string;
  name: string;
  abbreviation?: string;
  description?: string;
  isDefault?: boolean;
}

// ─── Shared row ───────────────────────────────────────────────────────────────

const UnitRow: React.FC<{
  unit: UnitOption;
  selected: boolean;
  onPress: () => void;
}> = ({ unit, selected, onPress }) => {
  const rowStyles = useThemedStyles(getRowStyles);
  return (
  <TouchableOpacity style={[rowStyles.row, selected && rowStyles.rowSelected]} onPress={onPress} activeOpacity={0.7}>
    <View style={rowStyles.body}>
      <Text style={[rowStyles.name, selected && rowStyles.nameSelected]}>{unit.name}</Text>
      {unit.abbreviation ? <Text style={rowStyles.abbr}>{unit.abbreviation}</Text> : null}
    </View>
    {selected && <Ionicons name="checkmark" size={18} color={colors.primary} />}
  </TouchableOpacity>
  );
};

const getRowStyles = () => StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm + 2,
    gap: spacing.sm,
  },
  rowSelected: {
    backgroundColor: withAlpha(colors.primary, 0.05),
  },
  body: { flex: 1 },
  name: {
    fontSize: typography.fontSize.base,
    color: colors.textDark,
    fontFamily: typography.fontFamily.regular,
  },
  nameSelected: {
    color: colors.primary,
    fontFamily: typography.fontFamily.medium,
  },
  abbr: {
    fontSize: typography.fontSize.xs,
    color: colors.textLight,
    fontFamily: typography.fontFamily.regular,
    marginTop: 1,
  },
});

// ─── Web inline dropdown ──────────────────────────────────────────────────────

const WebUnitPicker: React.FC<UnitPickerProps & {
  units: UnitOption[];
  loading: boolean;
  open: boolean;
  setOpen: (v: boolean) => void;
  search: string;
  setSearch: (v: string) => void;
  filtered: UnitOption[];
}> = ({ value, unitId, onSelect, placeholder, loading, open, setOpen, search, setSearch, filtered }) => {
  const rowStyles = useThemedStyles(getRowStyles);
  const webStyles = useThemedStyles(getWebStyles);
  return (
  <View style={webStyles.wrapper}>
    {/* Trigger */}
    <TouchableOpacity
      style={[webStyles.trigger, open && webStyles.triggerOpen]}
      onPress={() => setOpen(!open)}
      activeOpacity={0.8}
    >
      <Text style={[webStyles.triggerText, !value && webStyles.triggerPlaceholder]} numberOfLines={1}>
        {value || placeholder || 'Select unit'}
      </Text>
      <Ionicons name={open ? 'chevron-up' : 'chevron-down'} size={16} color={colors.textLight} />
    </TouchableOpacity>

    {/* Inline expanded list — pushes content down, no absolute positioning */}
    {open && (
      <View style={webStyles.dropdown}>
        <View style={webStyles.searchRow}>
          <Ionicons name="search-outline" size={16} color={colors.textLight} style={webStyles.searchIcon} />
          <TextInput
            style={webStyles.searchInput}
            value={search}
            onChangeText={setSearch}
            placeholder="Search units…"
            placeholderTextColor={colors.textLight}
            autoFocus
          />
          {search.length > 0 && (
            <TouchableOpacity onPress={() => setSearch('')}>
              <Ionicons name="close-circle" size={15} color={colors.textLight} />
            </TouchableOpacity>
          )}
        </View>
        <View style={webStyles.divider} />

        {loading ? (
          <View style={webStyles.stateRow}>
            <ActivityIndicator size="small" color={colors.primary} />
          </View>
        ) : filtered.length === 0 ? (
          <View style={webStyles.stateRow}>
            <Text style={webStyles.stateText}>{search ? 'No matching units' : 'No units defined yet'}</Text>
          </View>
        ) : (
          <ScrollView style={webStyles.list} nestedScrollEnabled showsVerticalScrollIndicator={false} keyboardShouldPersistTaps="handled">
            {unitId && (
              <TouchableOpacity style={rowStyles.row} onPress={() => { onSelect(null); setOpen(false); }} activeOpacity={0.7}>
                <Text style={[rowStyles.name, { color: colors.textLight }]}>— None —</Text>
              </TouchableOpacity>
            )}
            {filtered.map((u) => (
              <UnitRow
                key={u._id}
                unit={u}
                selected={u._id === unitId}
                onPress={() => { onSelect(u); setOpen(false); setSearch(''); }}
              />
            ))}
          </ScrollView>
        )}
      </View>
    )}
  </View>
  );
};

const getWebStyles = () => StyleSheet.create({
  wrapper: {},
  trigger: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1.5,
    borderColor: colors.border,
    borderRadius: designTokens.borderRadius.md,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    backgroundColor: colors.background.surface,
    gap: spacing.xs,
  },
  triggerOpen: {
    borderColor: colors.primary,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
  },
  triggerText: {
    flex: 1,
    fontSize: typography.fontSize.base,
    color: colors.textDark,
    fontFamily: typography.fontFamily.regular,
  },
  triggerPlaceholder: { color: colors.textLight },
  dropdown: {
    backgroundColor: colors.background.surface,
    borderWidth: 1.5,
    borderTopWidth: 0,
    borderColor: colors.primary,
    borderBottomLeftRadius: designTokens.borderRadius.md,
    borderBottomRightRadius: designTokens.borderRadius.md,
    ...designTokens.shadows.sm,
  },
  searchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs,
  },
  searchIcon: { marginRight: spacing.xs },
  searchInput: {
    flex: 1,
    fontSize: typography.fontSize.sm,
    color: colors.textDark,
    fontFamily: typography.fontFamily.regular,
    paddingVertical: spacing.xs,
    // @ts-ignore web only
    outlineStyle: 'none',
  },
  divider: { height: 1, backgroundColor: colors.border },
  list: { maxHeight: 220 },
  stateRow: {
    padding: spacing.lg,
    alignItems: 'center',
  },
  stateText: {
    fontSize: typography.fontSize.sm,
    color: colors.textLight,
    fontFamily: typography.fontFamily.regular,
  },
});

// ─── Mobile bottom-sheet ──────────────────────────────────────────────────────

const MobileUnitPicker: React.FC<UnitPickerProps & {
  units: UnitOption[];
  loading: boolean;
  open: boolean;
  setOpen: (v: boolean) => void;
  search: string;
  setSearch: (v: string) => void;
  filtered: UnitOption[];
}> = ({ value, unitId, onSelect, placeholder, loading, open, setOpen, search, setSearch, filtered }) => {
  const rowStyles = useThemedStyles(getRowStyles);
  const mobileStyles = useThemedStyles(getMobileStyles);
  return (
  <>
    <TouchableOpacity
      style={mobileStyles.trigger}
      onPress={() => setOpen(true)}
      activeOpacity={0.8}
    >
      <Text style={[mobileStyles.triggerText, !value && mobileStyles.triggerPlaceholder]} numberOfLines={1}>
        {value || placeholder || 'Select unit'}
      </Text>
      <Ionicons name="chevron-down" size={16} color={colors.textLight} />
    </TouchableOpacity>

    <Modal visible={open} animationType="slide" transparent onRequestClose={() => setOpen(false)}>
      <View style={mobileStyles.overlay}>
        <View style={mobileStyles.sheet}>
          <View style={mobileStyles.handle} />

          <View style={mobileStyles.header}>
            <Text style={mobileStyles.title}>Select Unit</Text>
            <TouchableOpacity onPress={() => { setOpen(false); setSearch(''); }} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
              <Ionicons name="close" size={24} color={colors.textDark} />
            </TouchableOpacity>
          </View>

          {/* Search */}
          <View style={mobileStyles.searchRow}>
            <Ionicons name="search-outline" size={16} color={colors.textLight} style={mobileStyles.searchIcon} />
            <TextInput
              style={mobileStyles.searchInput}
              value={search}
              onChangeText={setSearch}
              placeholder="Search units…"
              placeholderTextColor={colors.textLight}
            />
          </View>
          <View style={mobileStyles.divider} />

          {loading ? (
            <View style={mobileStyles.stateRow}>
              <ActivityIndicator size="large" color={colors.primary} />
            </View>
          ) : filtered.length === 0 ? (
            <View style={mobileStyles.stateRow}>
              <Text style={mobileStyles.stateText}>{search ? 'No matching units' : 'No units defined yet'}</Text>
            </View>
          ) : (
            <FlatList
              data={filtered}
              keyExtractor={(u) => u._id}
              ListHeaderComponent={unitId ? (
                <TouchableOpacity style={rowStyles.row} onPress={() => { onSelect(null); setOpen(false); setSearch(''); }} activeOpacity={0.7}>
                  <Text style={[rowStyles.name, { color: colors.textLight }]}>— None —</Text>
                </TouchableOpacity>
              ) : null}
              renderItem={({ item }) => (
                <UnitRow
                  unit={item}
                  selected={item._id === unitId}
                  onPress={() => { onSelect(item); setOpen(false); setSearch(''); }}
                />
              )}
              keyboardShouldPersistTaps="handled"
              contentContainerStyle={{ paddingBottom: 40 }}
            />
          )}
        </View>
      </View>
    </Modal>
  </>
  );
};

const getMobileStyles = () => StyleSheet.create({
  trigger: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1.5,
    borderColor: colors.border,
    borderRadius: designTokens.borderRadius.md,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    backgroundColor: colors.background.surface,
    gap: spacing.xs,
  },
  triggerText: {
    flex: 1,
    fontSize: typography.fontSize.base,
    color: colors.textDark,
    fontFamily: typography.fontFamily.regular,
  },
  triggerPlaceholder: { color: colors.textLight },
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.45)',
    justifyContent: 'flex-end',
  },
  sheet: {
    backgroundColor: colors.background.surface,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: '75%',
  },
  handle: {
    width: 40,
    height: 4,
    borderRadius: 2,
    backgroundColor: colors.border,
    alignSelf: 'center',
    marginTop: spacing.sm,
    marginBottom: spacing.xs,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  title: {
    fontSize: typography.fontSize.lg,
    fontWeight: '600',
    color: colors.textDark,
    fontFamily: typography.fontFamily.semibold,
  },
  searchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
  },
  searchIcon: { marginRight: spacing.xs },
  searchInput: {
    flex: 1,
    fontSize: typography.fontSize.base,
    color: colors.textDark,
    fontFamily: typography.fontFamily.regular,
    paddingVertical: spacing.xs,
  },
  divider: { height: 1, backgroundColor: colors.border },
  stateRow: {
    padding: spacing.xxl,
    alignItems: 'center',
  },
  stateText: {
    fontSize: typography.fontSize.base,
    color: colors.textLight,
    fontFamily: typography.fontFamily.regular,
  },
});

// ─── Main export ──────────────────────────────────────────────────────────────

export const UnitPicker: React.FC<UnitPickerProps> = (props) => {
  const [units, setUnits] = useState<UnitOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let cancelled = false;
    const fetchUnits = async () => {
      setLoading(true);
      try {
        const res = await apiService.getUnits({ limit: 200 });
        if (!cancelled && res.statusCode === 1000 && res.data) {
          setUnits(res.data.units || []);
        }
      } catch (_) {
        // silently ignore — picker will just show empty state
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchUnits();
    return () => { cancelled = true; };
  }, []);

  const filtered = search.trim()
    ? units.filter((u) => u.name.toLowerCase().includes(search.toLowerCase()) || (u.abbreviation || '').toLowerCase().includes(search.toLowerCase()))
    : units;

  const shared = { ...props, units, loading, open, setOpen, search, setSearch, filtered };

  if (Platform.OS === 'web') {
    return <WebUnitPicker {...shared} />;
  }
  return <MobileUnitPicker {...shared} />;
};
